'use client';

import { type HTMLAttributes, type ReactNode, type Ref, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import { useMergedDialogOpenProps } from '@interxfi/design-system/components/dialog';
import { useCopyToClipboardWithTooltip } from '../../../../util/copy';
import { getCtrlKeyIndicator, hasActivated } from '../../../hot-key/utils';
import { BaseLink, type BaseLinkProps, type LinkElementType } from '../../../link/base-link';
import { Tooltip } from '../../../tooltip/tooltip';
import { type MonoTypographyProps, Typography, type TypographyProps } from '../../../typography';
import { useTypographyContext } from '../../../typography/context';
import { getShortenedId } from '../shared';
import styles from './compact-id.module.scss';

const defaultTypographyProps: MonoTypographyProps = {
  variant: 'mono',
  size: '0.875rem',
  lineHeight: '1rem',
  // Note, changing wrap will require using a resize observer to update widths
  wrap: 'no-wrap',
};

// Compact IDs are always copyable
export interface CompactIDProps extends HTMLAttributes<HTMLButtonElement> {
  characters?: number;
  fillParent?: boolean;
  id: string | undefined;
  LinkElement?: LinkElementType;
  linkProps?: Partial<BaseLinkProps>;
  ref?: Ref<HTMLButtonElement>;
  renderWhenEmpty?: ReactNode;
  showClickable?: boolean;
  to?: string;
  typographyProps?: TypographyProps;
}

export function CompactID({
  characters,
  className,
  fillParent = true,
  id,
  LinkElement = 'a',
  linkProps,
  renderWhenEmpty = null,
  to,
  showClickable = Boolean(to),
  typographyProps = defaultTypographyProps,
}: CompactIDProps) {
  const { align, tone } = useTypographyContext() || {};
  const [isOpen, setIsOpen] = useMergedDialogOpenProps({});
  const renderedId = useMemo(() => (characters ? getShortenedId(id, characters) : id), [id, characters]);
  const { onCopy, copiedText } = useCopyToClipboardWithTooltip();
  const typography = useMemo(
    () =>
      ({
        ...typographyProps,
        variant: 'mono',
        lineHeight: typographyProps.lineHeight || to ? '1.5rem' : defaultTypographyProps.lineHeight,
        tone: typographyProps.tone ?? tone,
      }) as TypographyProps,
    [typographyProps, to, tone],
  );

  useEffect(() => {
    async function handleKeyDown(event: KeyboardEvent) {
      if (hasActivated(event, 'c', true)) {
        event.preventDefault();

        if (id) {
          await onCopy(id);
        }
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [id, isOpen, onCopy]);

  const builtClassName = clsx(
    styles.compactId,
    Boolean(id) && styles.hasContent,
    fillParent && styles.fillParent,
    characters !== undefined && styles.fixedWidth,
    styles[`align-${align}`],
    className,
  );

  const baseTextElement = (
    <Typography
      align={align}
      className={styles.idText}
      decoration={to && showClickable ? 'underline' : undefined}
      {...typography}
      inheritance="current"
    >
      {renderedId || renderWhenEmpty}
    </Typography>
  );

  return (
    <div className={styles.compactIdContainer}>
      <Tooltip
        activator={
          to ? (
            <BaseLink className={builtClassName} LinkElement={LinkElement} to={to} {...linkProps}>
              {baseTextElement}
            </BaseLink>
          ) : (
            <button className={builtClassName} type="button">
              {baseTextElement}
            </button>
          )
        }
        className={styles.tooltip}
        onOpenChange={setIsOpen}
        open={isOpen}
      >
        {copiedText ? 'Copied' : `${id} (${getCtrlKeyIndicator()} c to copy)`}
      </Tooltip>
    </div>
  );
}
